
/**
 * First we will load all of this project's JavaScript dependencies which
 * includes Vue and other libraries. It is a great starting point when
 * building robust, powerful web applications using Vue and Laravel.
 */

require('~/bootstrap');

import Vue from 'vue';
window.Vue = Vue;
require('vue-axios-interceptors');
window.axios = require('axios');
Vue.prototype.$eventBus = new Vue();


//System imports
import VueRouter from "vue-router";
import XDS from "@hyperionx/xds";
import VueClip from "vue-clip";
import VueScrollTo from 'vue-scrollto';
import {VueReCaptcha} from 'vue-recaptcha-v3';
import VueAnalytics from 'vue-analytics';
import IdleVue from "idle-vue";

Vue.use(VueReCaptcha, {
    siteKey: '6Lcb76gZAAAAAN_l40mSvAtdiuFC0k39vlRzNw74',
    autoHideBadge: true
});
Vue.use(VueRouter);
Vue.use(VueScrollTo);
Vue.use(VueClip);
Vue.use(XDS, {
    theme: {
        'primary': '#FFBF3F',
        'secondary': '#E0EC89',
        'tertiary': 'rgba(255, 255, 255, 0.1)',
        'accent': '#8D4D66',
        'error': '#DC000F',
        'info': '#4A9DD1',
        'success': '#0DA20D',
        'warning': '#FAAA00',
        'cancel': '#E1E4EB',
        'red': '#DC000F',
        'orange': '#FFA700',
        'yellow': '#FFCE00',
        'green': '#0DA20D',
        'blue': '#0857C3',
        'plum': '#8D4D66',
        'ocean': '#1E9FD7',
        'olive': '#41807F',
        'gold': '#998542',
        'mustard': '#FFBF3F',
        'poppy': '#F9423A',
        'teal': '#7CE0D3',
        'cobalt': '#0857C3',
        'grey': {
            'base': '#707070',
            'lighten1': '#787A80',
            'lighten2': '#AFAFB3',
            'lighten3': '#E6E6E6',
            'lighten4': '#F0F0F5',
            'lighten5': '#F2F3F7',
            'darken1': '#575759',
            'darken2': '#46454D',
            'darken3': '#2D2D2E',
            'darken4': '#1A1A1A'
        }
    },
    iconfont: "fa"
});

import Default from '~/layouts/Default.vue';
import Login from '~/layouts/Login.vue';

Vue.component('app', require('~/App.vue').default);

// Layouts
Vue.component('default-layout', Default);
Vue.component('login-layout', Login);

//Components
Vue.component('loading-spinner', require('~/components/Shared/UIComponents/LoadingSpinner').default);
Vue.component('field-group', require('~/components/Shared/UIComponents/FieldGroup').default);
Vue.component('question-section', require('~/components/Shared/UIComponents/QuestionSection').default);
Vue.component('x-currency-text-field', require('~/components/Shared/UIComponents/XCurrencyTextField').default);
Vue.component('error-messages', require('~/components/Shared/Interceptors/ErrorMessages').default);


import moment from "moment";
Vue.prototype.$moment = moment;

// Load up filters
import filters from '~/filters/filters';
Vue.use(filters);

// Routes
import routes from '~/routes/routes';
let router = new VueRouter({
    mode: 'history',
    routes,
});
// eslint-disable-next-line no-process-env
const isProd = process.env.NODE_ENV === 'production';
Vue.use(VueAnalytics, {
    id: 'UA-151752887-8',
    router,
    debug: {
        enabled: !isProd,
        sendHitTask: isProd
    },
    disabled: window.globalUserConsentPromise.then(() => false)
});

Vue.use(IdleVue, {
    eventEmitter: Vue.prototype.$eventBus,
    idleTime: 1000 * 60 * 25, //25 minutes
    startAtIdle: false
});

const data = {
    info: {},
    user: {},
    scopes: [],
    role: '',
    notifications: null,
    waitingPeriods: []
};

//Setup auth service
import { HyperionAuth } from '@hyperionx/hyperion-x-auth';
Vue.prototype.$hyperionAuth = new HyperionAuth();


router.beforeEach(async (to, from, next) => {

    await router.app.$hyperionAuth.initialize();

    const routeScopes = to.meta.scopes;

    // Unprotected route
    if (typeof routeScopes === 'undefined') {
        return next();
    }

    if (typeof router.app.$root.user.company === 'undefined') {
        await router.app.loadData();
    }

    const userScope = router.app.getUserScope();

    if (routeScopes.includes(userScope)) {
        return next();
    }

    next({ path: '/unauthorised'})
});


const app = new Vue({
    el: '#application',
    router,
    data: data,
    async created() {
        await this.$hyperionAuth.initialiseEnvironment(this.unauthenticated, this.unauthorised, this.redirectionToUpdateExpiredPassword);

        if (localStorage.getItem('hyx-auth') && this.$router.currentRoute.name !== 'login') {
            this.loadData();
            this.loadWaitingPeriods();
            this.loadCountries();
        }
    },
    methods: {
        unauthenticated() {
            this.$router.push('/login');
        },
        unauthorised() {
            this.$router.push('/unauthorised');
        },
        redirectionToUpdateExpiredPassword(redirectTo) {
            this.$router.push({ name: redirectTo });
        },
        async loadData() {
            this.$root.user = (await axios.get(this.$root.api('user'), {responseType:'json'})).data;
        },
        api(path) {
            console.log("API: " + path + " ==> " + this.$hyperionAuth.getEnvironment()['MIX_APP_API_DOMAIN'] + path);
            return this.$hyperionAuth.getEnvironment()['MIX_APP_API_DOMAIN'] + path;
        },
        env(key) {
            return this.$hyperionAuth.getEnvironment()[key];
        },
        getUserScope() {
            if (this.userIsClientUser()) {
                return 'client';
            }
            if (this.userIsCaptiveUser()) {
                return 'captive-insurer';
            }
            if (this.userIsInsurerUser()) {
                return 'insurer'
            }
            if (this.userIsProducingBrokerUser()) {
                return this.userIsBrokerAdmin() ? 'admin-producing-broker' : 'producing-broker';
            }
            if (this.userIsPlacingBrokerUser()) {
                return this.userIsBrokerAdmin() ? 'admin-placing-broker' : 'placing-broker';
            }

            return null;
        },
        getUserRole() {
            return this.$root.user.company.company_type;
        },
        userIsSuperUser() {
            return this.$root.user.role.id === 1 && this.$root.user.role.name === "user";
        },
        userIsBrokerAdmin() {
            return this.$root.hasOwnProperty('user') && this.$root.user.role_name === 'broker-admin';
        },
        userIsCaptiveUser() {
            return this.$root.hasOwnProperty('user') && this.$root.user.company?.company_type === 'captive_insurer';
        },
        userIsInsurerUser() {
            return this.$root.hasOwnProperty('user') && this.$root.user.company.company_type === 'insurer';
        },
        userIsBrokerUser() {
            return this.$root.hasOwnProperty('user') &&
            (this.$root.user.company.company_type === 'placing_broker' || this.$root.user.company.company_type === 'producing_broker');
        },
        userIsPlacingBrokerUser() {
            return this.$root.hasOwnProperty('user') && this.$root.user.company?.company_type === 'placing_broker';
        },
        userIsProducingBrokerUser() {
            return this.$root.hasOwnProperty('user') && this.$root.user.company.company_type === 'producing_broker';
        },
        userIsClientUser() {
            return this.$root.hasOwnProperty('user') && this.$root.user.company?.company_type === 'client';
        },
        async loadWaitingPeriods() {
            this.$root.waitingPeriods = (await axios.get(this.$root.api('waiting-periods'), {responseType:'json'})).data.data;
        },
        async loadCountries() {
            this.$root.countries = (await axios.get(this.$root.api('country'), {responseType:'json'})).data.data;
        }
    }
});

//Expose for debug
window.app = app;
